/**
 * login / sign_up — successful authentication only. (#162, GA4-E4-07)
 *
 * We cannot measure account creation rate, compare conversion between logged-in and guest customers,
 * or tell whether the login prompt is friction in the purchase flow. For a repeat-purchase grocery
 * business this is the most useful non-ecommerce event pair there is.
 *
 * 🔴 THE HARD REQUIREMENT: NEVER REPORT A FAILED LOGIN AS A SUCCESS (#162 step 4)
 * A submit handler cannot know whether authentication succeeded — Shopify answers a bad password by
 * re-rendering the same page with form errors, which looks identical to a submit from the client's
 * point of view. So a click NEVER fires an event here. The only thing that fires one is the
 * SERVER-RENDERED truth on the following page: `page_data.customer_logged_in === true`, published by
 * `analytics-datalayer.liquid` (#141) straight from Liquid's `customer` object.
 *
 * A submit records only the *method* — an intent marker in sessionStorage. It is consumed on the next
 * page where Liquid confirms a session, and discarded otherwise. Concretely:
 *
 *   success → redirect to /account, `customer` exists    → marker consumed, `login` fires
 *   failure → /account/login re-renders, no `customer`   → marker sits unused, nothing fires
 *   already signed in, browsing                          → no marker, nothing fires (step 7)
 *
 * That last line is why the marker is deleted the instant it is used: an existing session must not
 * re-fire `login` on every page load.
 *
 * ENTRY POINTS COVERED
 * All of them, without editing a single section. Shopify's `{% form %}` tag stamps `customer_login`
 * and `create_customer` onto the rendered `<form>`, and the listener is delegated on the document, so
 * it covers `/account/login`, `/account/register`, and the account popup in
 * `snippets/popup-account.liquid` that `theme.liquid` renders on every page — including the two
 * duplicate login forms `login.liquid` happens to contain.
 *
 * ⛔ NOT covered: the checkout login prompt. Checkout is rendered by Shopify on its own domain, where
 * no theme asset runs. It belongs to the checkout pixel (#143), not here.
 *
 * OTP / PHONE
 * The store runs a phone-OTP app that calls `window.runOnSMLEvent(event, data)` — see the WebEngage
 * block in `templates/customers/login.liquid`. We wrap that callback rather than replace it, so its
 * success events set the method to `otp`. The wrapper always calls the original, inside a try/catch:
 * breaking the store's existing CRM integration to add an analytics event would be a bad trade.
 *
 * ⚠️ An OTP success that never navigates reports on the visitor's NEXT page view rather than
 * immediately, because the marker still waits for Liquid to confirm the session. Deliberate: it keeps
 * exactly one rule for what counts as success, and the alternative — trusting the app's client-side
 * event — is the "infer it from a click" failure mode step 4 rules out.
 *
 * PRIVACY (#162 step 5, and the GA4-E8-01 exposure)
 * This file sets no `user_id` and reads no identifier at all. It used to push `page_data.customer_id`
 * as `user_id`, but #211 moved `customer_id` out of `page_data` into the consent-gated `user_data`
 * push, so that line had nothing to read. `user_id` now rides on the `user_data` push itself
 * (`snippets/analytics-datalayer.liquid`, #663), which is the only place allowed to emit it.
 */
(function () {
  'use strict';

  window.dataLayer = window.dataLayer || [];

  var KEY = 'om_auth_intent';
  var TTL_MS = 15 * 60 * 1000;   // a stale intent must not attribute a much later sign-in

  function store() {
    try { return window.sessionStorage; } catch (e) { return null; }   // Safari private mode throws
  }

  function pageData() {
    var dl = window.dataLayer || [];
    for (var i = dl.length - 1; i >= 0; i--) {
      if (dl[i] && dl[i].page_data) return dl[i].page_data;
    }
    return null;
  }

  function remember(intent, method) {
    var s = store();
    if (!s) return;
    try {
      s.setItem(KEY, JSON.stringify({ intent: intent, method: method, at: new Date().getTime() }));
    } catch (e) { /* quota or disabled — we simply do not report this sign-in */ }
  }

  function takeMarker() {
    var s = store();
    if (!s) return null;
    var raw;
    try { raw = s.getItem(KEY); } catch (e) { return null; }
    if (!raw) return null;

    // Consume before validating: a malformed or expired marker must not be able to sit there and
    // attribute some unrelated future session.
    try { s.removeItem(KEY); } catch (e) { /* nothing useful to do */ }

    var marker;
    try { marker = JSON.parse(raw); } catch (e) { return null; }
    if (!marker || !marker.intent) return null;
    if (!marker.at || (new Date().getTime() - marker.at) > TTL_MS) return null;
    return marker;
  }

  function report(marker, pd) {
    var event = marker.intent === 'sign_up' ? 'sign_up' : 'login';

    window.dataLayer.push({
      event: event,
      method: marker.method || 'email',
      // Already on page_data every page (#141); repeated on the event so the custom dimension is
      // populated on the hit that matters, per step 6.
      customer_type: pd.customer_type || ''
    });
  }

  // ---------------------------------------------------------------- fire, if the server agrees
  var pd = pageData();
  if (pd && pd.customer_logged_in === true) {
    var marker = takeMarker();
    if (marker) report(marker, pd);
  }

  // ---------------------------------------------------------------- record intent (never an event)
  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (!form || form.nodeName !== 'FORM') return;

    var id = form.getAttribute('id') || '';
    var action = form.getAttribute('action') || '';

    // Match on either, because `login.liquid` renders two `customer_login` forms on one page and
    // duplicate ids are unreliable to select on even though the attribute still reads correctly.
    if (id === 'customer_login' || /\/account\/login(\?|$)/.test(action)) {
      remember('login', 'email');
    } else if (id === 'create_customer' || /\/account(\?|$)/.test(action)) {
      remember('sign_up', 'email');
    }
  }, true);

  // ---------------------------------------------------------------- OTP / phone app
  // Wrap rather than replace: this callback is the store's WebEngage integration and must keep
  // working exactly as before.
  var previous = window.runOnSMLEvent;
  window.runOnSMLEvent = function (event, data) {
    try {
      if (event === 'LOGIN_SUCCESS' || event === 'USER_LOGGED_IN' || event === 'LOGIN_SUCCESSFUL') {
        remember('login', 'otp');
      } else if (event === 'REGISTER_SUCCESS' || event === 'USER_REGISTERED_SUCCESSFULLY') {
        remember('sign_up', 'otp');
      }
    } catch (err) { /* analytics must never break the login flow */ }

    if (typeof previous === 'function') return previous.apply(this, arguments);
  };
})();
